import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, refreshSession, session } from './api';

export interface Me { userId: string; email: string | null; name: string | null; permissions: string[] }
interface AuthState {
  status: 'loading' | 'signed-out' | 'signed-in';
  me: Me | null;
  can: (perm: string) => boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = useCallback(async () => {
    setMe(await api<Me>('/admin/me'));
    setStatus('signed-in');
  }, []);

  useEffect(() => {
    session.onExpired(() => { session.set(null); setMe(null); setStatus('signed-out'); });
    // Restore the session from the refresh cookie on page load.
    refreshSession().then((ok) => (ok ? loadMe() : setStatus('signed-out'))).catch(() => setStatus('signed-out'));
  }, [loadMe]);

  const value = useMemo<AuthState>(() => ({
    status, me,
    can: (perm) => !!me && (me.permissions.includes('*') || me.permissions.includes(perm)),
    login: async (identifier, password) => {
      const t = await api<{ accessToken: string }>('/admin/auth/login', { method: 'POST', body: { identifier, password } });
      session.set(t.accessToken);
      await loadMe();
    },
    logout: async () => {
      try { await api('/admin/auth/logout', { method: 'POST', body: {} }); } finally {
        session.set(null); setMe(null); setStatus('signed-out');
      }
    },
  }), [status, me, loadMe]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
